import jsPDF from 'jspdf';
import digisakaLogo from '../../../assets/logo-transparent.png';
import { cadiCategoryFromMean } from '../constants/cadiDroughtLayers';
import type {
  MultitemporalZonalLayer,
  MunicipalityZonalRow,
} from './multitemporalZonalAnalytics';

const GREEN: [number, number, number] = [18, 106, 55];
const TEXT: [number, number, number] = [30, 41, 59];
const SUB: [number, number, number] = [91, 102, 116];
const BORDER: [number, number, number] = [226, 232, 240];
const BG_ROW: [number, number, number] = [248, 250, 252];

let cachedLogoDataUrl: string | null | undefined;

async function loadDigisakaLogoDataUrl(): Promise<string | null> {
  if (cachedLogoDataUrl !== undefined) return cachedLogoDataUrl;
  try {
    const res = await fetch(digisakaLogo);
    const blob = await res.blob();
    cachedLogoDataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(new Error('logo read failed'));
      reader.readAsDataURL(blob);
    });
  } catch {
    cachedLogoDataUrl = null;
  }
  return cachedLogoDataUrl;
}

function hexToRgb(hex: string): [number, number, number] {
  const n = Number.parseInt(hex.replace('#', ''), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function cadiCategory(value: number): { label: string; color: [number, number, number] } {
  const cat = cadiCategoryFromMean(value);
  return { label: cat.label, color: hexToRgb(cat.color) };
}

function pestCategory(value: number): { label: string; color: [number, number, number] } {
  if (value < 0.2) return { label: 'Very low', color: [160, 160, 80] };
  if (value < 0.4) return { label: 'Low', color: [254, 217, 118] };
  if (value < 0.6) return { label: 'Moderate', color: [253, 141, 60] };
  if (value < 0.8) return { label: 'High', color: [240, 59, 32] };
  return { label: 'Very high', color: [128, 0, 38] };
}

function categoryFor(layer: MultitemporalZonalLayer, value: number) {
  return layer === 'cadi' ? cadiCategory(value) : pestCategory(value);
}

function fmtNum(n: number, digits = 3): string {
  if (!Number.isFinite(n)) return '—';
  return n.toFixed(digits);
}

export type MultitemporalZonalPdfInput = {
  layer: MultitemporalZonalLayer;
  province: string;
  frameLabel: string;
  pestLabel?: string;
  rows: MunicipalityZonalRow[];
  selectedMunicipality?: string | null;
};

function drawBrandHeader(
  pdf: jsPDF,
  subtitle: string,
  metaLine: string,
  logoDataUrl: string | null
): number {
  const pageW = pdf.internal.pageSize.getWidth();
  const marginX = 16;
  const headerH = 32;

  pdf.setFillColor(...GREEN);
  pdf.rect(0, 0, pageW, headerH, 'F');

  const logoSize = 18;
  let textX = marginX;
  if (logoDataUrl) {
    try {
      pdf.addImage(logoDataUrl, 'PNG', marginX, (headerH - logoSize) / 2, logoSize, logoSize);
      textX = marginX + logoSize + 5;
    } catch {
      /* text only */
    }
  }

  pdf.setTextColor(255, 255, 255);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(16);
  pdf.text('Digisaka', textX, 13);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  pdf.text(subtitle, textX, 21);

  pdf.setFillColor(241, 245, 242);
  pdf.rect(0, headerH, pageW, 10, 'F');
  pdf.setTextColor(...SUB);
  pdf.setFontSize(7.5);
  pdf.text(metaLine, marginX, headerH + 6.5);

  return headerH + 10;
}

function drawFooter(pdf: jsPDF, pageNum: number, totalPages: number): void {
  const pageW = pdf.internal.pageSize.getWidth();
  const marginX = 16;
  const y = 287;
  pdf.setDrawColor(...BORDER);
  pdf.line(marginX, y - 6, pageW - marginX, y - 6);
  pdf.setTextColor(...SUB);
  pdf.setFontSize(8);
  pdf.text('Digisaka · support@digisaka.app', marginX, y);
  pdf.text(`Page ${pageNum} of ${totalPages}`, pageW - marginX, y, { align: 'right' });
}

/**
 * A4 Digisaka PDF: province municipality analytics for Drought or Pest outlook.
 */
export async function downloadMultitemporalZonalReportPdf(
  input: MultitemporalZonalPdfInput
): Promise<void> {
  const { layer, province, frameLabel, pestLabel, rows, selectedMunicipality } = input;
  const logoDataUrl = await loadDigisakaLogoDataUrl();
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageW = pdf.internal.pageSize.getWidth();
  const marginX = 16;
  const contentW = pageW - marginX * 2;
  const pageBottom = 278;

  const isDrought = layer === 'cadi';
  const title = isDrought
    ? 'Drought outlook — province report'
    : 'Pest Occurrence Probability Index — province report';
  const indexLabel = isDrought ? 'Drought index' : 'Occurrence probability';
  const generatedAt = new Date().toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
  const metaLine = [
    generatedAt,
    province,
    frameLabel,
    pestLabel || null,
  ]
    .filter(Boolean)
    .join('  ·  ');

  const withStats = rows.filter((r) => r.stats != null);
  const means = withStats.map((r) => r.stats!.mean);
  const provinceMean =
    means.length > 0 ? means.reduce((a, b) => a + b, 0) / means.length : NaN;
  const provinceMin =
    withStats.length > 0 ? Math.min(...withStats.map((r) => r.stats!.min)) : NaN;
  const provinceMax =
    withStats.length > 0 ? Math.max(...withStats.map((r) => r.stats!.max)) : NaN;
  const provinceCat = Number.isFinite(provinceMean)
    ? categoryFor(layer, provinceMean)
    : null;

  const categoryCounts = new Map<string, number>();
  for (const row of withStats) {
    const cat = categoryFor(layer, row.stats!.mean);
    categoryCounts.set(cat.label, (categoryCounts.get(cat.label) || 0) + 1);
  }

  // Sort: highest occurrence probability / drought first
  const sorted = [...rows].sort((a, b) => {
    const am = a.stats?.mean ?? -1;
    const bm = b.stats?.mean ?? -1;
    return bm - am;
  });

  let headerEnd = drawBrandHeader(pdf, title, metaLine, logoDataUrl);
  let y = headerEnd + 8;
  let pageNum = 1;

  // Summary
  pdf.setTextColor(...TEXT);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(12);
  pdf.text(province, marginX, y);
  y += 6;
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  pdf.setTextColor(...SUB);
  pdf.text(
    isDrought
      ? 'Municipality drought outlook summaries for the selected period.'
      : `Municipality Pest Occurrence Probability Index summaries${pestLabel ? ` (${pestLabel})` : ''} for the selected period.`,
    marginX,
    y
  );
  y += 8;

  // Summary cards
  const cardW = (contentW - 8) / 3;
  const cards: Array<{ label: string; value: string; sub?: string }> = [
    {
      label: 'Province average',
      value: fmtNum(provinceMean),
      sub: provinceCat?.label,
    },
    {
      label: 'Lowest / Highest',
      value: `${fmtNum(provinceMin)} / ${fmtNum(provinceMax)}`,
    },
    {
      label: 'Municipalities',
      value: `${withStats.length} / ${rows.length}`,
      sub: 'with data',
    },
  ];
  cards.forEach((card, i) => {
    const x = marginX + i * (cardW + 4);
    pdf.setFillColor(232, 243, 236);
    pdf.roundedRect(x, y, cardW, 18, 2, 2, 'F');
    pdf.setTextColor(...SUB);
    pdf.setFontSize(7);
    pdf.setFont('helvetica', 'normal');
    pdf.text(card.label, x + 3, y + 5);
    pdf.setTextColor(...TEXT);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(11);
    pdf.text(card.value, x + 3, y + 12);
    if (card.sub) {
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(7);
      pdf.setTextColor(...SUB);
      pdf.text(card.sub, x + 3, y + 16);
    }
  });
  y += 24;

  // Category breakdown
  if (categoryCounts.size > 0) {
    pdf.setTextColor(...TEXT);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(10);
    pdf.text('Status breakdown', marginX, y);
    y += 5;
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    const breakdown = [...categoryCounts.entries()]
      .map(([label, count]) => `${label}: ${count}`)
      .join('   ·   ');
    pdf.setTextColor(...SUB);
    const lines = pdf.splitTextToSize(breakdown, contentW);
    pdf.text(lines, marginX, y);
    y += lines.length * 4 + 4;
  }

  if (selectedMunicipality) {
    const sel = rows.find((r) => r.municipality === selectedMunicipality);
    if (sel?.stats) {
      const cat = categoryFor(layer, sel.stats.mean);
      pdf.setFillColor(255, 251, 235);
      pdf.roundedRect(marginX, y, contentW, 12, 2, 2, 'F');
      pdf.setTextColor(...TEXT);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(8);
      pdf.text(
        `Selected: ${selectedMunicipality} — ${fmtNum(sel.stats.mean)} (${cat.label})`,
        marginX + 3,
        y + 7.5
      );
      y += 16;
    }
  }

  // Table header helper
  const colMuni = marginX;
  const colMean = marginX + contentW * 0.48;
  const colMin = marginX + contentW * 0.62;
  const colMax = marginX + contentW * 0.74;
  const colStatus = marginX + contentW * 0.86;

  const drawTableHeader = () => {
    pdf.setFillColor(241, 245, 242);
    pdf.rect(marginX, y - 4, contentW, 8, 'F');
    pdf.setTextColor(...SUB);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(7.5);
    pdf.text('Municipality', colMuni + 1, y);
    pdf.text(indexLabel, colMean, y, { align: 'right' });
    pdf.text('Min', colMin, y, { align: 'right' });
    pdf.text('Max', colMax, y, { align: 'right' });
    pdf.text('Status', colStatus, y, { align: 'right' });
    y += 6;
  };

  pdf.setTextColor(...TEXT);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(10);
  pdf.text('Municipality summaries', marginX, y);
  y += 8;
  drawTableHeader();

  for (let i = 0; i < sorted.length; i++) {
    if (y > pageBottom) {
      pdf.addPage();
      pageNum += 1;
      headerEnd = drawBrandHeader(pdf, title, metaLine, logoDataUrl);
      y = headerEnd + 8;
      drawTableHeader();
    }

    const row = sorted[i];
    if (i % 2 === 1) {
      pdf.setFillColor(...BG_ROW);
      pdf.rect(marginX, y - 3.5, contentW, 6.5, 'F');
    }

    pdf.setTextColor(...TEXT);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(7.5);
    const name = pdf.splitTextToSize(row.municipality, contentW * 0.44);
    pdf.text(name[0], colMuni + 1, y);

    if (row.stats) {
      const cat = categoryFor(layer, row.stats.mean);
      pdf.text(fmtNum(row.stats.mean), colMean, y, { align: 'right' });
      pdf.text(fmtNum(row.stats.min), colMin, y, { align: 'right' });
      pdf.text(fmtNum(row.stats.max), colMax, y, { align: 'right' });
      pdf.setTextColor(...cat.color);
      pdf.setFont('helvetica', 'bold');
      pdf.text(cat.label, colStatus, y, { align: 'right' });
    } else {
      pdf.setTextColor(...SUB);
      pdf.text('—', colMean, y, { align: 'right' });
      pdf.text('—', colMin, y, { align: 'right' });
      pdf.text('—', colMax, y, { align: 'right' });
      pdf.text(row.error ? 'No data' : '—', colStatus, y, { align: 'right' });
    }
    y += 6.5;
  }

  // Notes
  if (y > pageBottom - 20) {
    pdf.addPage();
    pageNum += 1;
    headerEnd = drawBrandHeader(pdf, title, metaLine, logoDataUrl);
    y = headerEnd + 8;
  } else {
    y += 6;
  }
  pdf.setDrawColor(...BORDER);
  pdf.line(marginX, y, pageW - marginX, y);
  y += 6;
  pdf.setTextColor(...TEXT);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(9);
  pdf.text('How to read this report', marginX, y);
  y += 5;
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8);
  pdf.setTextColor(...SUB);
  const notes = isDrought
    ? [
        'Values near 0 mean little drought stress; values near 1 mean severe agricultural drought is more likely.',
        'Municipality averages are sampled from the drought outlook map for the selected period.',
        'Walk higher-index areas and compare with field conditions before taking action.',
      ]
    : [
        'Higher Pest Occurrence Probability Index values mean greater likelihood of pest or disease occurrence for the selected pest and period.',
        'Municipality averages are sampled from the Pest Occurrence Probability Index map for the selected quarter/period.',
        'Use this with field scouting — the map is a guide, not a substitute for field checks.',
      ];
  for (const note of notes) {
    const lines = pdf.splitTextToSize(`• ${note}`, contentW);
    pdf.text(lines, marginX, y);
    y += lines.length * 4 + 1;
  }

  const totalPages = pdf.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    pdf.setPage(p);
    drawFooter(pdf, p, totalPages);
  }

  const safeProvince = province.replace(/[^\w.-]+/g, '_');
  const kind = isDrought ? 'drought' : 'pest_occurrence_probability';
  pdf.save(`Digisaka_${kind}_outlook_${safeProvince}.pdf`);
}
